import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import notificationService from "../../services/notificationService";
import "./NotificationPanel.css";

export const NotificationPanel = ({ userId, isOpen, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!userId) {
      return;
    }
    setLoading(true);
    const unsubscribe = notificationService.subscribeToNotifications(
      userId,
      (notifs) => {
        setNotifications(notifs);
        const unread = notifs.filter((n) => !n.isRead).length;
        setUnreadCount(unread);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(notificationId);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead(userId);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationService.deleteNotification(notificationId);
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleDeleteAll = async () => {
    if (window.confirm("Delete all notifications?")) {
      try {
        await notificationService.deleteAllNotifications(userId);
      } catch (error) {
        console.error("Error deleting all notifications:", error);
      }
    }
  };

  const handleNotificationClick = (notif) => {
    const postType = notif?.postType;
    const postId = notif?.postId;
    let targetPath = "/community";
    if (postType === "question" && postId) {
      targetPath = `/question/${postId}`;
    } else if (postType === "contribution" && postId) {
      targetPath = `/contribution/${postId}`;
    } else if ((postType === "word_comment_reply" || postType === "word_comment") && postId) {
      targetPath = `/dictionary/english-kumouni/${postId}/${postId}-meaning-in-kumouni`;
    } else if ((postType === "wod_reply" || postType === "wod_like") && postId) {
      // Navigate to Word of the Day page
      targetPath = `/word-of-the-day/${postId}`;
    } else if (postId) {
      targetPath = `/contribution/${postId}`;
    }
    navigate(targetPath);
    onClose();
    // Mark as read when clicked
    if (!notif.isRead) {
      handleMarkAsRead(notif.id, { stopPropagation: () => {} });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.seconds * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  if (!isOpen) return null;

  return (
    <div className="notification-panel-overlay" onClick={onClose}>
      <div
        className="notification-panel-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="notification-panel-header">
          <div className="notification-panel-title">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </div>
          <div className="notification-panel-header-actions">
            {unreadCount > 0 && (
              <button
                className="notification-header-action-btn"
                onClick={handleMarkAllAsRead}
                title="Mark all as read"
              >
                Mark all read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                className="notification-header-action-btn"
                onClick={handleDeleteAll}
                title="Delete all notifications"
              >
                Clear all
              </button>
            )}
          </div>
          <button
            className="notification-panel-close"
            onClick={onClose}
            aria-label="Close notifications"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="notification-panel-content">
          {loading ? (
            <div className="notification-loading">Loading...</div>
          ) : notifications && notifications.length > 0 ? (
            <ul className="notification-list">
              {notifications.map((notif) => (
                <li
                  key={notif.id}
                  className={`notification-item ${
                    notif.isRead ? "read" : "unread"
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  {/* Status Indicator */}
                  <div
                    className="notification-status-indicator"
                    aria-label={notif.isRead ? "Read" : "Unread"}
                  />
                  <div className="notification-item-content">
                    <p className="notification-message">{notif.message}</p>
                    {notif.commentContent && (
                      <p className="notification-comment">
                        {notif.commentContent}
                      </p>
                    )}
                    {notif.postTitle && (
                      <p className="notification-post-title">
                        {notif.postTitle}
                      </p>
                    )}
                  </div>
                  <div className="notification-item-time">
                    {formatDate(notif.createdAt)}
                  </div>

                  {/* Actions (Hidden until hover) */}
                  <div
                    className="notification-item-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!notif.isRead && (
                      <button
                        className="notification-btn mark-read"
                        onClick={(e) => handleMarkAsRead(notif.id, e)}
                        title="Mark as read"
                        aria-label="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="notification-btn delete"
                      onClick={(e) => handleDeleteNotification(notif.id, e)}
                      title="Delete"
                      aria-label="Delete notification"
                    >
                      ✕
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="notification-empty">
              <p>You're all caught up</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationPanel;

